'use client'

import { useRef } from 'react'
import NextLink from 'next/link'
import ColorScheme from '@/components/colorScheme'
import ArrowRight from '@/components/ArrowRight'
import type { ColorScheme as ColorSchemeType } from '@/sanity.types'
import type { YouthSportsFrontmatter } from '@/lib/schemas/youth-sports'
import { posthog } from '@/lib/posthog'
import { useYouthSportsAnalytics } from './YouthSportsAnalyticsContext'
import { renderInlineMarkdown } from '@/lib/inline-markdown'

const INITIAL_SHOW = 5

const CARD_SCHEMES: ColorSchemeType[] = [
  'pea',
  'blueberry',
  'royal',
  'plum',
  'sky',
  'grape',
  'lilac',
  'tangerine',
]

type Props = {
  profiles: YouthSportsFrontmatter['programProfiles']
  heading?: string
}

export default function ProgramProfilesExpander({ profiles, heading }: Props) {
  const hasTrackedExpansion = useRef(false)
  const { sport, city, pageType, variant } = useYouthSportsAnalytics()

  const labelClass = 'eyebrow mb-1.5 font-semibold!'
  const sorted = [...profiles].sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0))
  const initialProfiles = sorted.slice(0, INITIAL_SHOW)
  const additionalProfiles = sorted.slice(INITIAL_SHOW)
  const remaining = sorted.length - INITIAL_SHOW

  const renderProfile = (p: (typeof sorted)[number], i: number) => {
    const href = p.website && p.website.length > 0
      ? (p.website.startsWith('http') ? p.website : `https://${p.website}`)
      : null

    return (
      <ColorScheme
        key={p.name}
        colorScheme={CARD_SCHEMES[i % CARD_SCHEMES.length]}
      >
        <article
          aria-labelledby={`program-${i}-name`}
          className="bg-(--color-background) text-(--color-foreground) rounded p-5 md:p-7.5"
        >
          {/* Name + metadata — flex column avoids text-box-trim overlap from .h4 */}
          <div className="flex flex-col gap-2 mb-5">
            <h3 id={`program-${i}-name`} className="ys-section-h">
              {href ? (
                <NextLink
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start justify-between gap-3 group"
                >
                  <span className="min-w-0 group-hover:underline decoration-1 underline-offset-4">{p.name}</span>
                  <span
                    className="shrink-0 flex items-center justify-center size-8 rounded-full
                               bg-(--color-foreground) text-(--color-background)"
                    aria-hidden="true"
                  >
                    <ArrowRight className="size-4" />
                  </span>
                </NextLink>
              ) : (
                p.name
              )}
            </h3>

            <p className="text-sm">
              {p.type}
              {p.ageRange ? <> · Ages {p.ageRange}</> : null}
              {p.costRange ? <> · {renderInlineMarkdown(p.costRange)}</> : null}
              {p.location ? <> · {p.location}</> : null}
            </p>
          </div>

          {/* Programs + coaching — stacked rows */}
          <div className="flex flex-col gap-4 mb-5 pt-5 border-t-2 border-(--color-foreground)">
            <div>
              <p className={labelClass}>Programs</p>
              <p className="text-base">{renderInlineMarkdown(p.programs)}</p>
            </div>
            {p.coaching && (
              <div>
                <p className={labelClass}>Coaching</p>
                <p className="text-base">{renderInlineMarkdown(p.coaching)}</p>
              </div>
            )}
          </div>

          {/* Pros + cons */}
          <div className="grid md:grid-cols-2 gap-x-7.5 gap-y-4 mb-5 pt-5 border-t border-(--color-foreground)/30">
            <div>
              <p className={labelClass}>Pros</p>
              <p className="text-base">{renderInlineMarkdown(p.pros)}</p>
            </div>
            <div>
              <p className={labelClass}>Cons</p>
              <p className="text-base">{renderInlineMarkdown(p.cons)}</p>
            </div>
          </div>

          {/* Best fit */}
          <div className="pt-5 border-t border-(--color-foreground)/30">
            <p className={labelClass}>Best fit</p>
            <p className="text-base">{renderInlineMarkdown(p.bestFit)}</p>
          </div>

        </article>
      </ColorScheme>
    )
  }

  return (
    <section aria-labelledby="program-profiles-heading">
      <h2 id="program-profiles-heading" className="ys-section-h mb-7.5">
        {heading ?? 'Program Profiles'}
      </h2>

      <div className="flex flex-col gap-5">
        {initialProfiles.map(renderProfile)}
      </div>

      {remaining > 0 && (
        <details
          className="group"
          onToggle={(event) => {
            if (!event.currentTarget.open || hasTrackedExpansion.current) return

            hasTrackedExpansion.current = true
            posthog.capture('program_profiles_expand', {
              sport,
              city,
              pageType,
              source: 'web_marketing',
              ...(variant ? { variant } : {}),
              profiles_revealed: remaining,
            })
          }}
        >
          <summary
            className="button mt-7.5 py-3 w-full flex items-center justify-center cursor-pointer
                       bg-(--color-foreground) text-(--color-background)
                       list-none [&::-webkit-details-marker]:hidden"
          >
            <span className="group-open:hidden">
              Show {remaining} more program{remaining === 1 ? '' : 's'}
            </span>
            <span className="hidden group-open:inline">Show fewer programs</span>
          </summary>

          <div className="flex flex-col gap-5 mt-5">
            {additionalProfiles.map((profile, index) =>
              renderProfile(profile, index + INITIAL_SHOW)
            )}
          </div>
        </details>
      )}
    </section>
  )
}
